/* eslint-disable */
import * as React from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {forOwn, forEach} from 'lodash-es';

import adminRoutes from 'routes/admin.routes';
import NotAuthorized from 'components/notAuthorized';

import {useFindByIdQuery, useInsertMutation, useUpdateMutation} from 'features/marketingRegions/marketingRegionsApi';

import {IMarketingRegion} from 'models/marketingRegions';

import {marketingRegionValidator} from '../validators';

import {
  Box,
  Button,
  Cluster,
  Cover,
  FormItem,
  Grid,
  Heading,
  Select,
  Spinner,
  Stack,
  Template,
  TextInput,
  Paragraph,
  Notification,
  useValidateForm,
  ISelectOption,
  Toast,
  trimModel,
} from '@pluto-tv/assemble';

import {useAppPermissions} from 'app/permissions';

export default (): JSX.Element => {
  const {id}: {id: string} = useParams();
  const history = useHistory();
  const {ableTo, permissions} = useAppPermissions();

  // if (!ableTo('MARKETING_REGION_VIEW')) {
  //   return <NotAuthorized />;
  // }
  const cantModify =
    (id === 'new' && !ableTo('MARKETING_REGION_CREATE')) || (id !== 'new' && !ableTo('MARKETING_REGION_EDIT'));

  const [isSaving, setIsSaving] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const [updateMarketingRegion] = useUpdateMutation();
  const [insertMarketingRegion] = useInsertMutation();

  const {
    data: item,
    isError: marketingRegionIsError,
    isLoading: marketingRegionIsLoading,
  } = useFindByIdQuery(id, {refetchOnMountOrArgChange: true});

  const {
    model: marketingRegionModel,
    form: userForm,
    onBlur,
    onChange,
    pristineModel,
    setFields,
    setModel,
    state: formState,
  } = useValidateForm<IMarketingRegion>(marketingRegionValidator);

  const cancelHandler = () => {
    history.push(adminRoutes.paths.marketingRegionsListPage);
  };

  React.useEffect(() => {
    if (item) {
      setModel(item);
    }
  }, [item, setModel]);

  const saveHandler = async () => {
    if (isSaving || cantModify) {
      return;
    }

    setIsSaving(true);

    try {
      if (id === 'new') {
        await onMarketingRegionCreate({
          ...marketingRegionModel,
          permissions: marketingRegionModel.permissions?.filter(p => p !== ''),
        } as IMarketingRegion);
      } else {
        await onMarketingRegionUpdate(id, marketingRegionModel as IMarketingRegion);
      }
    } catch (e) {
      const validationErrors = (e as any).data?.validationErrors;

      if (validationErrors) {
        forOwn(validationErrors, (val: string[]) => {
          forEach(val, errorMsg => Toast.error('Validation Error', errorMsg));
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onMarketingRegionCreate = async (entity: IMarketingRegion) => {
    const trimmed = trimModel(entity, 'name', 'description');
    const result = await insertMarketingRegion(trimmed).unwrap();
    Toast.success('Success', 'Marketing Region Created');
    history.push(adminRoutes.paths.marketingRegionsEditPage.replace(':id', result._id));
  };

  const onMarketingRegionUpdate = async (id: string, entity: IMarketingRegion) => {
    const trimmed = trimModel(entity, 'name', 'description');
    const result = await updateMarketingRegion({id, marketingRegion: trimmed}).unwrap();
    Toast.success('Success', 'Marketing Region Updated');
    history.push(adminRoutes.paths.marketingRegionsEditPage.replace(':id', result._id));
  };

  if (isError) {
    return (
      <Box fullHeight={true} padding={{mobile: 'medium', wide: 'xlarge'}}>
        <Notification type='warning'>
          <Paragraph>There was an error loading the Marketing Region.</Paragraph>
        </Notification>
      </Box>
    );
  }

  const {marketingRegion, isFetching} = {
    marketingRegion:
      id !== 'new'
        ? {
            _id: '61c4f43a47935d24df43b304',
            regionName: 'Brazil',
            slug: 'BR',
            territories: ['Brazil'],
            permissions: [],
          }
        : {
            regionName: '',
            slug: '',
            territories: [],
            permissions: [],
          },
    isFetching: false,
  };

  if (isFetching) {
    return (
      <Box fullHeight={true}>
        <Spinner center={true} size='xlarge' />
      </Box>
    );
  }

  return (
    <>
      <Cover
        scrolling={true}
        gutter='large'
        coverTemplateHeight='100%'
        paddingX={{mobile: 'medium', wide: 'large'}}
        paddingTop={{mobile: 'medium', wide: 'large'}}
      >
        <Template label='header'>
          <Grid>
            <Cluster justify='space-between' align='end' space='medium'>
              <Heading level='h1'>{id === 'new' ? 'New Marketing Region' : marketingRegion.regionName}</Heading>
            </Cluster>
            <div></div>
          </Grid>
        </Template>
        <Template label='cover'>
          <Grid>
            <Box background='pewter' borderTop={true} borderSize='2px' borderColor='cavern' fullHeight={true}>
              <Box paddingY={{mobile: 'medium', wide: 'large'}} paddingX={{mobile: 'medium', wide: 'xlarge'}}>
                <Stack space='xlarge'>
                  <Heading level='h3' color='secondary'>
                    Details
                  </Heading>
                  <Stack space='small'>
                    <FormItem
                      label='Region Name'
                      required={true}
                      // permission={permissions.MARKETING_REGION_EDIT}
                      state={id === 'new' ? undefined : 'disabled'}
                    >
                      <TextInput onChange={val => onChange('regionName', val)} value={marketingRegion.regionName} />
                    </FormItem>
                    <FormItem label='Region Slug' required={true} state={id === 'new' ? undefined : 'disabled'}>
                      <Select
                        options={[{label: 'BR'}, {label: 'DE'}, {label: 'DK'}]}
                        value={{label: marketingRegion.slug}}
                      />
                    </FormItem>
                    <FormItem label='Associated Territories'>
                      <Select
                        multiselect={true}
                        addAll={true}
                        clearable={true}
                        options={[
                          {label: 'Brazil'},
                          {label: 'Germany'},
                          {label: 'Switzerland'},
                          {label: 'Austria'},
                          {label: 'Denmark'},
                          {label: 'Faroe Islands'},
                          {label: 'Greenland'},
                        ]}
                        value={marketingRegion.territories.map(region => ({label: region}))}
                      />
                    </FormItem>
                  </Stack>
                </Stack>
              </Box>
            </Box>
            <div></div>
          </Grid>
        </Template>
        <Template label='footer'>
          <Box background='onyx' paddingX='small' paddingY='small' marginX={{mobile: 'none', wide: 'largeNegative'}}>
            <Cluster justify='space-between'>
              <div></div>
              <Cluster space='xxxsmall'>
                <Button ghost={true} onClick={() => cancelHandler()} id='discard'>
                  Discard
                </Button>
                <Button
                  type='primary'
                  id='save'
                  onClick={() => saveHandler()}
                  state={!formState.isValid || !formState.isDirty ? 'disabled' : isSaving ? 'thinking' : ''}
                  // permission={permissions.MARKETING_REGION_EDIT}
                >
                  Save Changes
                </Button>
              </Cluster>
            </Cluster>
          </Box>
        </Template>
      </Cover>
    </>
  );
};
